import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header, OffcanvasMenu, SupportModal } from '../common';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';

interface AppLayoutProps {
  children: React.ReactNode;
}

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

/**
 * AppLayout component that wraps all non-auth pages with header and offcanvas menu
 */
export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true); // Default to collapsed
  const [isMobile, setIsMobile] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  // Detect mobile vs desktop and set appropriate default state
  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Set default state: collapsed on mobile, expanded on desktop
      setIsMenuCollapsed(mobile);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  const handleMenuToggle = () => {
    setIsMenuCollapsed(!isMenuCollapsed);
  };

  const handleNavigation = (path: string) => {
    if (path === '/logout') {
      // Handle logout action
      signOut()
        .then(() => {
          navigate('/');
        })
        .catch((error) => {
          console.error('Logout failed:', error);
        });
    } else {
      // Regular navigation
      navigate(path);
      // Auto-collapse on mobile after navigation
      if (isMobile) {
        setIsMenuCollapsed(true);
      }
    }
  };

  return (
    <LayoutContainer>
      <OffcanvasMenu
        isCollapsed={isMenuCollapsed}
        onToggle={handleMenuToggle}
        onNavigate={handleNavigation}
        onSupportClick={() => setIsSupportModalOpen(true)}
      />
      <Header
        isMenuCollapsed={isMenuCollapsed}
        onMenuToggle={handleMenuToggle}
        notificationCount={0}
      />
      {children}
      <SupportModal 
        isOpen={isSupportModalOpen} 
        onClose={() => setIsSupportModalOpen(false)} 
      />
    </LayoutContainer>
  );
};

export default AppLayout;
